import React, { useEffect, useState, useCallback } from 'react';
import { advanceAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Wallet, Plus, ChevronDown, ChevronUp } from 'lucide-react';

export default function AdvancesPage() {
  const { user } = useAuth();
  const [advances, setAdvances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [form, setForm] = useState({ amount: '', purpose: '', notes: '' });
  const [settleForm, setSettleForm] = useState({ amount: '', description: '' });
  const [settlingId, setSettlingId] = useState(null);

  const fetchAdvances = useCallback(async () => {
    try {
      const res = await advanceAPI.list();
      setAdvances(res.data.advances);
    } catch (err) { toast.error('Failed to load advances'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAdvances(); }, [fetchAdvances]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.amount || !form.purpose) { toast.error('Amount and purpose required'); return; }
    try {
      await advanceAPI.create({ amount: parseFloat(form.amount), purpose: form.purpose, notes: form.notes });
      toast.success('Advance request created');
      setShowForm(false);
      setForm({ amount: '', purpose: '', notes: '' });
      fetchAdvances();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed');
    }
  };

  const handleSettle = async (advanceId) => {
    if (!settleForm.amount || !settleForm.description) { toast.error('Amount and description required'); return; }
    try {
      await advanceAPI.settle(advanceId, {
        amount: parseFloat(settleForm.amount),
        description: settleForm.description,
        billAttached: false
      });
      toast.success('Settlement recorded');
      setSettlingId(null);
      setSettleForm({ amount: '', description: '' });
      fetchAdvances();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed');
    }
  };

  const statusStyles = {
    Requested: 'bg-amber-50 text-amber-700 border-amber-200',
    Approved: 'bg-green-50 text-green-700 border-green-200',
    'Partially Settled': 'bg-blue-50 text-blue-700 border-blue-200',
    Settled: 'bg-slate-100 text-slate-600 border-slate-200',
    Rejected: 'bg-red-50 text-red-700 border-red-200',
    Cancelled: 'bg-slate-100 text-slate-500 border-slate-200',
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-900"></div></div>;

  return (
    <div className="space-y-4" data-testid="advances-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Wallet className="w-5 h-5 text-slate-700" />
          <h1 className="text-xl font-bold tracking-tight text-slate-900">Advances</h1>
        </div>
        <Button size="sm" onClick={() => setShowForm(!showForm)} className="bg-slate-900 hover:bg-slate-800 text-white text-xs" data-testid="request-advance-btn">
          <Plus className="w-3.5 h-3.5 mr-1" /> Request Advance
        </Button>
      </div>

      {/* New advance form */}
      {showForm && (
        <form onSubmit={handleCreate} className="bg-white border border-slate-200 rounded-lg p-4 space-y-3" data-testid="advance-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Amount (₹)</Label>
              <Input type="number" value={form.amount} onChange={(e) => setForm(f => ({ ...f, amount: e.target.value }))} placeholder="0.00" className="mt-1" data-testid="advance-amount-input" />
            </div>
            <div>
              <Label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Purpose</Label>
              <Input value={form.purpose} onChange={(e) => setForm(f => ({ ...f, purpose: e.target.value }))} placeholder="Reason for advance" className="mt-1" data-testid="advance-purpose-input" />
            </div>
          </div>
          <div className="flex gap-2">
            <Button type="submit" size="sm" className="bg-slate-900 hover:bg-slate-800 text-white text-xs" data-testid="submit-advance-btn">Submit Request</Button>
            <Button type="button" size="sm" variant="outline" onClick={() => setShowForm(false)} className="text-xs">Cancel</Button>
          </div>
        </form>
      )}

      {/* Advances list */}
      <div className="space-y-2">
        {advances.map(a => (
          <div key={a._id} className="bg-white border border-slate-200 rounded-lg" data-testid={`advance-card-${a.advanceId}`}>
            <div
              className="p-3 flex items-center justify-between cursor-pointer hover:bg-slate-50"
              onClick={() => setExpandedId(expandedId === a._id ? null : a._id)}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-slate-900">{a.advanceId}</span>
                  <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${statusStyles[a.status] || ''}`}>{a.status}</span>
                </div>
                <p className="text-xs text-slate-500 mt-0.5">{a.purpose}</p>
                <p className="text-[10px] text-slate-400 mt-0.5">{a.employee?.name}</p>
              </div>
              <div className="flex items-center gap-3 ml-3">
                <div className="text-right">
                  <p className="text-sm font-bold text-slate-900">₹{a.amount?.toLocaleString('en-IN')}</p>
                  {a.remainingBalance > 0 && a.status !== 'Requested' && a.status !== 'Rejected' && (
                    <p className="text-[10px] text-amber-600">Remaining: ₹{a.remainingBalance?.toLocaleString('en-IN')}</p>
                  )}
                </div>
                {expandedId === a._id ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
              </div>
            </div>

            {expandedId === a._id && (
              <div className="border-t border-slate-200 p-3 space-y-3">
                {/* Settlement history */}
                {a.settlements?.length > 0 && (
                  <div>
                    <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Settlements</p>
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-slate-50">
                          <th className="text-left px-2 py-1">Date</th>
                          <th className="text-left px-2 py-1">Description</th>
                          <th className="text-right px-2 py-1">Amount</th>
                          <th className="text-center px-2 py-1">Bill</th>
                        </tr>
                      </thead>
                      <tbody>
                        {a.settlements.map((s) => (
                          <tr key={s._id || `${s.settledAt}-${s.amount}`} className="border-t border-slate-100">
                            <td className="px-2 py-1">{new Date(s.settledAt).toLocaleDateString('en-IN')}</td>
                            <td className="px-2 py-1">{s.description}</td>
                            <td className="px-2 py-1 text-right font-medium">₹{s.amount?.toLocaleString('en-IN')}</td>
                            <td className="px-2 py-1 text-center">{s.billAttached ? 'Yes' : 'No'}</td>
                          </tr>
                        ))}
                        <tr className="border-t border-slate-200 font-medium">
                          <td colSpan="2" className="px-2 py-1">Total Settled</td>
                          <td className="px-2 py-1 text-right">₹{a.totalSettled?.toLocaleString('en-IN')}</td>
                          <td></td>
                        </tr>
                      </tbody>
                    </table>
                    <div className="mt-2 text-xs space-y-0.5">
                      <p>Remaining: <span className="font-medium">₹{a.remainingBalance?.toLocaleString('en-IN')}</span></p>
                      {a.excessAmount > 0 && <p className="text-red-600">Excess: ₹{a.excessAmount?.toLocaleString('en-IN')}</p>}
                    </div>
                  </div>
                )}

                {/* Settlement form */}
                {['Approved', 'Partially Settled'].includes(a.status) && (
                  settlingId === a._id ? (
                    <div className="bg-slate-50 rounded-md p-3 space-y-2">
                      <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Add Settlement</p>
                      <div className="grid grid-cols-2 gap-2">
                        <Input type="number" value={settleForm.amount} onChange={(e) => setSettleForm(f => ({ ...f, amount: e.target.value }))} placeholder="Amount" className="h-7 text-xs" data-testid="settle-amount-input" />
                        <Input value={settleForm.description} onChange={(e) => setSettleForm(f => ({ ...f, description: e.target.value }))} placeholder="Description" className="h-7 text-xs" data-testid="settle-description-input" />
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => handleSettle(a._id)} className="bg-slate-900 hover:bg-slate-800 text-white text-[10px] h-6" data-testid="submit-settle-btn">Submit</Button>
                        <Button size="sm" variant="outline" onClick={() => setSettlingId(null)} className="text-[10px] h-6">Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => { setSettlingId(a._id); setSettleForm({ amount: '', description: '' }); }} className="text-xs h-7" data-testid={`settle-advance-${a.advanceId}`}>
                      Add Settlement
                    </Button>
                  )
                )}
              </div>
            )}
          </div>
        ))}
        {advances.length === 0 && (
          <div className="bg-white border border-slate-200 rounded-lg p-8 text-center">
            <p className="text-sm text-slate-400">No advances found</p>
          </div>
        )}
      </div>
    </div>
  );
}
